/**
 * Utility functions for QR tag node visibility based on user types
 */

// User types as defined in the schema
export type UserType = 'public' | 'installatore' | 'rivenditore' | 'produttore'

/**
 * Determines the current user type based on authentication and role information
 * @param isAuthenticated - Whether the user is authenticated
 * @param roleCode - The user's role code (from auth store)
 * @param isInstaller - Whether the user is an installer
 * @returns The user type string
 */
export function getCurrentUserType(
  isAuthenticated: boolean,
  roleCode: string | null,
  isInstaller: boolean
): UserType {
  // If not authenticated, user is public
  if (!isAuthenticated) {
    return 'public'
  }

  // If user is installer, return installatore
  if (isInstaller) {
    return 'installatore'
  }

  // Map role codes to user types
  switch (roleCode) {
    case 'SELLER':
    case 'RESELLER':
      return 'rivenditore'
    case 'MANUFACTURER':
    case 'PRODUCER':
      return 'produttore'
    case 'INSTALLER':
      return 'installatore'
    default:
      // Default to public for unknown roles
      return 'public'
  }
}

/**
 * Checks if a node should be visible to the current user type
 * @param isHiddenFor - Array of user types for which the node should be hidden
 * @param currentUserType - The current user's type
 * @returns true if the node should be visible, false if it should be hidden
 */
export function isNodeVisible(
  isHiddenFor: string[] | undefined,
  currentUserType: UserType
): boolean {
  // If isHiddenFor is undefined or empty, the node is visible to all
  if (!isHiddenFor || isHiddenFor.length === 0) {
    return true
  }

  // Check if current user type is in the hidden list
  return !isHiddenFor.includes(currentUserType)
}

/**
 * Filters sections based on visibility rules
 * @param sections - Object containing sections (from data.nodes)
 * @param currentUserType - The current user's type
 * @returns Filtered object with only visible sections
 */
export function filterVisibleSections<T extends { isHiddenFor?: string[] }>(
  sections: Record<string, T>,
  currentUserType: UserType
): Record<string, T> {
  const filteredSections: Record<string, T> = {}

  for (const [key, section] of Object.entries(sections)) {
    if (isNodeVisible(section.isHiddenFor, currentUserType)) {
      filteredSections[key] = section
    }
  }

  return filteredSections
}

/**
 * Filters data items within a section based on visibility rules
 * @param items - Array of data items
 * @param currentUserType - The current user's type
 * @returns Filtered array with only visible items
 */
export function filterVisibleDataItems<T extends { isHiddenFor?: string[] }>(
  items: T[],
  currentUserType: UserType
): T[] {
  return items.filter(item => isNodeVisible(item.isHiddenFor, currentUserType))
}

/**
 * Filters wizard questions based on visibility rules
 * @param questions - Array of wizard questions
 * @param currentUserType - The current user's type
 * @returns Filtered array with only visible questions
 */
export function filterVisibleWizardQuestions<T extends { isHiddenFor?: string[] }>(
  questions: T[],
  currentUserType: UserType
): T[] {
  return questions.filter(question => isNodeVisible(question.isHiddenFor, currentUserType))
}

/**
 * Recursively filters a complete section object with its children based on visibility rules
 * This function handles the complete filtering of sections including their nested children elements
 * @param section - Section object with potential children or questions
 * @param currentUserType - The current user's type
 * @returns Filtered section with only visible children/questions, or null if section itself should be hidden
 */
export function filterVisibleSection<
  TSection extends {
    isHiddenFor?: string[]
    children?: Array<{ isHiddenFor?: string[] }>
    questions?: Array<{ isHiddenFor?: string[] }>
  }
>(
  section: TSection,
  currentUserType: UserType
): TSection | null {
  // First check if the section itself should be visible
  if (!isNodeVisible(section.isHiddenFor, currentUserType)) {
    return null
  }

  // Create a copy of the section to avoid mutating the original
  const filteredSection = { ...section }

  // Filter children if they exist
  if (section.children && section.children.length > 0) {
    filteredSection.children = filterVisibleDataItems(section.children, currentUserType)
  }

  // Filter questions if they exist
  if (section.questions && section.questions.length > 0) {
    filteredSection.questions = filterVisibleWizardQuestions(section.questions, currentUserType)
  }

  return filteredSection
}

/**
 * Filters sections and their children recursively based on visibility rules
 * This is the main function to use for filtering complete QR tag node data
 * @param sections - Object containing sections (from data.nodes)
 * @param currentUserType - The current user's type
 * @returns Filtered object with only visible sections and their visible children
 */
export function filterVisibleSectionsRecursive<
  TSection extends {
    isHiddenFor?: string[]
    children?: Array<{ isHiddenFor?: string[] }>
    questions?: Array<{ isHiddenFor?: string[] }>
  }
>(
  sections: Record<string, TSection>,
  currentUserType: UserType
): Record<string, TSection> {
  const filteredSections: Record<string, TSection> = {}

  for (const [key, section] of Object.entries(sections)) {
    const filteredSection = filterVisibleSection(section, currentUserType)
    if (filteredSection) {
      filteredSections[key] = filteredSection
    }
  }

  return filteredSections
}